/**
 * AccountPanel — S4 #account
 * canonical: lines 2188–2210
 *
 * Live:
 *   - Shows current email (from /api/auth/me query cache)
 *   - Delete account → 3-step confirmation → DELETE /api/auth/account → logout
 *     Step 1: click "Delete account" → confirm form appears
 *     Step 2: type DELETE in input → "Permanently delete" button enables
 *     Step 3: click "Permanently delete" → final confirmation warning appears
 *     Step 4: click "Yes, delete everything" → API call → logout
 * ComingSoon: Change email, Change password, Enable 2FA, Review sessions
 *
 * GATE-B FIX: Added 'final-confirm' state — prevents immediate deletion
 * on single "Permanently delete" click. Matches expected UX from checklist.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, TOKEN_KEY } from '../../../api/client'
import { deleteAccount, requestDataExport, loadSettings, saveSettings } from '../../../api/settings'
import { updateDisplayName } from '../../../api/users'
import { useAuth } from '../../../context/AuthContext'
import ComingSoonModal from '../../../components/ComingSoonModal'

interface UserInfo { email?: string; name?: string }

type DeleteStep = 'idle' | 'confirm' | 'final-confirm' | 'deleting' | 'error'

export default function AccountPanel() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { refetch: refetchAuth } = useAuth()
  const [displayName, setDisplayName] = useState('')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameErr, setNameErr] = useState('')
  const [nameOk, setNameOk] = useState(false)
  const [showModal,    setShowModal]    = useState(false)
  const [modalTitle,   setModalTitle]   = useState('')
  const [modalBody,    setModalBody]    = useState('')
  const [deleteStep,   setDeleteStep]   = useState<DeleteStep>('idle')
  const [deleteErr,    setDeleteErr]    = useState('')
  const [confirmInput, setConfirmInput] = useState('')
  const [exportBusy,   setExportBusy]   = useState(false)
  const [exportMsg,    setExportMsg]    = useState('')
  const [exportErr,    setExportErr]    = useState('')

  const { data: user } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => apiFetch<UserInfo>('GET', '/api/auth/me'),
    staleTime: 300_000,
  })

  const { data: settings, refetch: refetchSettings } = useQuery({
    queryKey: ['settings'],
    queryFn: loadSettings,
    staleTime: 60_000,
  })
  const showTestProjects = settings?.aria_preferences?.show_test_projects === true
  const [testToggleBusy, setTestToggleBusy] = useState(false)

  const currentName = displayName || user?.name || ''

  async function handleSaveDisplayName() {
    const trimmed = currentName.trim()
    if (trimmed.length < 2 || trimmed.length > 80) {
      setNameErr('Display name must be 2–80 characters')
      return
    }
    setNameSaving(true)
    setNameErr('')
    setNameOk(false)
    try {
      await updateDisplayName(trimmed)
      await queryClient.invalidateQueries({ queryKey: ['auth-me'] })
      await refetchAuth()
      setDisplayName(trimmed)
      setNameOk(true)
    } catch (e) {
      setNameErr((e as Error).message || 'Could not save display name')
    } finally {
      setNameSaving(false)
    }
  }

  function openModal(title: string, body: string) {
    setModalTitle(title); setModalBody(body); setShowModal(true)
  }

  function handleCancelDelete() {
    setDeleteStep('idle')
    setConfirmInput('')
    setDeleteErr('')
  }

  /** Step 2 → 3: "Permanently delete" clicked — show final confirmation */
  function handleRequestFinalConfirm() {
    if (confirmInput !== 'DELETE') return
    setDeleteStep('final-confirm')
  }

  async function handleExportData() {
    setExportBusy(true)
    setExportMsg('')
    setExportErr('')
    try {
      const msg = await requestDataExport()
      setExportMsg(msg)
    } catch (e) {
      setExportErr((e as Error).message || 'Export request failed')
    } finally {
      setExportBusy(false)
    }
  }

  /** Step 4: "Yes, delete everything" clicked — actually call the API */
  async function handleConfirmedDelete() {
    setDeleteStep('deleting')
    try {
      await deleteAccount()
      localStorage.removeItem(TOKEN_KEY)
      window.dispatchEvent(new CustomEvent('auth:logout'))
      navigate('/login', { replace: true })
    } catch (e) {
      setDeleteErr((e as Error).message || 'Delete failed')
      setDeleteStep('error')
    }
  }

  return (
    <div id="account" className="panel" data-testid="panel-account" style={{ marginTop: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Account &amp; security</div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Show test projects</div>
          <div className="set-row-sh">Include projects marked TEST in your project list. KPIs, Brief, statements, and Books sync always exclude them.</div>
        </div>
        <div className="set-row-r">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={showTestProjects}
              disabled={testToggleBusy}
              data-testid="show-test-projects-toggle"
              onChange={async (e) => {
                setTestToggleBusy(true)
                try {
                  const prefs = {
                    action_mode: 'ask' as const,
                    confirmation_threshold: 5000,
                    soren_enabled: false,
                    voice_prefs: { voice_on_default: false, read_aloud: false },
                    ...(settings?.aria_preferences ?? {}),
                    show_test_projects: e.target.checked,
                  }
                  await saveSettings({ aria_preferences: prefs })
                  await refetchSettings()
                  await queryClient.invalidateQueries({ queryKey: ['projects'] })
                } finally {
                  setTestToggleBusy(false)
                }
              }}
            />
            Visible in project list
          </label>
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Display name</div>
          <div className="set-row-st">Shown in the top bar and reports</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <input
              className="fld-i"
              data-testid="account-display-name"
              value={currentName}
              onChange={e => { setDisplayName(e.target.value); setNameOk(false) }}
              placeholder="Your name"
              maxLength={80}
              style={{ flex: '1 1 180px', minWidth: 0 }}
            />
            <button
              type="button"
              className="btn b-dk btn-sm"
              data-testid="account-display-name-save"
              disabled={nameSaving || !currentName.trim() || currentName.trim() === (user?.name ?? '')}
              onClick={handleSaveDisplayName}
            >
              {nameSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {nameErr && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6 }}>{nameErr}</div>}
          {nameOk && <div style={{ fontSize: 11, color: 'var(--green, #15803d)', marginTop: 6 }}>Display name updated</div>}
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Email</div>
          <div className="set-row-st" data-testid="account-email">{user?.email ?? '—'}</div>
        </div>
        <div className="set-row-r">
          <button className="btn b-ol btn-sm" data-testid="change-email-btn"
            onClick={() => openModal('Change email', 'Email change requires verification of your new address. This feature is coming soon — for now, contact support@varshyl.com to update your email.')}>
            Change
          </button>
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Password</div>
          <div className="set-row-st">Use "Forgot password" on the login page to reset</div>
        </div>
        <div className="set-row-r">
          <button className="btn b-ol btn-sm" data-testid="change-password-btn"
            onClick={() => openModal('Change password', 'In-app password change is coming soon (B-45). To reset your password now, log out and use "Forgot password" on the login screen.')}>
            Change
          </button>
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Two-factor authentication</div>
          <div className="set-row-st">Not enabled · adds SMS or app-based 2FA</div>
        </div>
        <div className="set-row-r">
          <button className="btn b-dk btn-sm" data-testid="enable-2fa-btn"
            onClick={() => openModal('Two-factor authentication', '2FA is coming in a future security update. Your account is protected by your password in the meantime.')}>
            Enable 2FA
          </button>
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Active sessions</div>
          <div className="set-row-st">Manage devices logged in to your account</div>
        </div>
        <div className="set-row-r">
          <button className="btn b-ol btn-sm" data-testid="review-sessions-btn"
            onClick={() => openModal('Active sessions', 'Session management is coming soon. To log out all devices, change your password.')}>
            Review
          </button>
        </div>
      </div>

      <div className="set-row">
        <div className="set-row-l">
          <div className="set-row-tt">Export my data</div>
          <div className="set-row-st">Download a ZIP of your projects, pay apps, and uploaded files (link emailed, valid 24 hours)</div>
          {exportMsg && <div style={{ fontSize: 11, color: 'var(--green, #15803d)', marginTop: 6 }}>{exportMsg}</div>}
          {exportErr && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6 }}>{exportErr}</div>}
        </div>
        <div className="set-row-r">
          <button
            type="button"
            className="btn b-ol btn-sm"
            data-testid="export-data-btn"
            disabled={exportBusy}
            onClick={handleExportData}
          >
            {exportBusy ? 'Requesting…' : 'Export my data'}
          </button>
        </div>
      </div>

      {/* Delete account — 3-step confirmation */}
      <div className="set-row" style={{ borderTop: '1px solid var(--line)', marginTop: 6, paddingTop: 14 }}
        data-testid="delete-account-section">
        <div className="set-row-l">
          <div className="set-row-tt" style={{ color: 'var(--red)' }}>Delete account</div>
          <div className="set-row-st">Permanently deletes your account and all project data. This cannot be undone.</div>

          {/* Step 2: Type DELETE to unlock */}
          {(deleteStep === 'confirm' || deleteStep === 'error') && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 6 }}>
                Type <strong>DELETE</strong> to confirm permanent deletion of all your data.
              </div>
              <input
                className="fld-i"
                value={confirmInput}
                onChange={e => setConfirmInput(e.target.value)}
                placeholder="DELETE"
                style={{ marginBottom: 8 }}
                data-testid="delete-confirm-input"
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn b-red btn-sm"
                  disabled={confirmInput !== 'DELETE'}
                  onClick={handleRequestFinalConfirm}
                  data-testid="delete-confirm-btn"
                >
                  Permanently delete
                </button>
                <button className="btn b-ol btn-sm" onClick={handleCancelDelete}
                  data-testid="delete-cancel-btn">
                  Cancel
                </button>
              </div>
              {deleteStep === 'error' && (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6 }}>{deleteErr}</div>
              )}
            </div>
          )}

          {/* Step 3: Final confirmation warning */}
          {(deleteStep === 'final-confirm' || deleteStep === 'deleting') && (
            <div style={{
              marginTop: 10,
              padding: '12px 14px',
              background: 'var(--red-bg, #fff5f5)',
              border: '1.5px solid var(--red)',
              borderRadius: 'var(--radius-s)',
            }}
              data-testid="delete-final-confirm"
            >
              <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 6, fontSize: 13 }}>
                Last chance — this cannot be undone
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>
                Your account, all projects, invoices, and documents will be <strong>permanently deleted</strong>.
                There is no recovery path.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn b-red btn-sm"
                  disabled={deleteStep === 'deleting'}
                  onClick={handleConfirmedDelete}
                  data-testid="delete-final-btn"
                >
                  {deleteStep === 'deleting' ? 'Deleting…' : 'Yes, delete everything'}
                </button>
                <button className="btn b-ol btn-sm" onClick={handleCancelDelete}
                  disabled={deleteStep === 'deleting'}
                  data-testid="delete-cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="set-row-r">
          {deleteStep === 'idle' && (
            <button className="btn b-red btn-sm" data-testid="delete-account-btn"
              onClick={() => setDeleteStep('confirm')}>
              Delete account
            </button>
          )}
        </div>
      </div>

      <ComingSoonModal isOpen={showModal} onClose={() => setShowModal(false)}
        title={modalTitle} body={modalBody} />
    </div>
  )
}
